(() => {
  if (window.__tlA11yEnhanced) return;
  window.__tlA11yEnhanced = true;

  const sliderSelector = [
    '.tl-slider-section embla-slider',
    '.tl-usp-strip embla-slider',
    '.tl-category-showcase embla-slider',
    '.tl-deals-showcase embla-slider',
    '.tl-brand-showcase embla-slider',
    '.tl-blog-showcase embla-slider',
    '.tl-promise-showcase embla-slider',
    '.tl-reviews-showcase embla-slider'
  ].join(',');

  const syncSlides = (slider) => {
    const slides = Array.from(slider.querySelectorAll('embla-slider-slide'));
    const total = slides.length;

    slides.forEach((slide, index) => {
      const hidden = slide.getAttribute('aria-hidden') === 'true';
      slide.toggleAttribute('inert', hidden);
      slide.inert = hidden;
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      if (!slide.hasAttribute('aria-label') && total > 1) {
        slide.setAttribute('aria-label', `${index + 1} of ${total}`);
      }
    });
  };

  const isAutoplaySlider = (slider) => {
    return slider.hasAttribute('autoplay') || slider.dataset.autoplay === 'true';
  };

  const enhanceSlider = (slider) => {
    if (!(slider instanceof HTMLElement) || slider.dataset.tlA11yEnhanced === 'true') return;
    slider.dataset.tlA11yEnhanced = 'true';
    slider.setAttribute('aria-roledescription', 'carousel');
    if (!slider.hasAttribute('role')) slider.setAttribute('role', 'region');

    syncSlides(slider);

    // reduced-motion pause
    if (
      isAutoplaySlider(slider) &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('embla-slider').then(() => slider.pause?.());
      } else {
        slider.pause?.();
      }
    }

    slider.addEventListener('focusin', () => {
      if (!isAutoplaySlider(slider) || slider.hasAttribute('paused')) return;
      slider.dataset.tlPausedByFocus = 'true';
      slider.pause?.();
    });

    slider.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && slider.contains(next)) return;
      if (slider.dataset.tlPausedByFocus !== 'true') return;
      delete slider.dataset.tlPausedByFocus;
      slider.play?.();
    });

    const observer = new MutationObserver(() => syncSlides(slider));
    observer.observe(slider, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true
    });
  };

  // inert follows aria-hidden
  const syncInert = (slider) => {
    slider.querySelectorAll('embla-slider-slide').forEach((slide) => {
      slide.inert = slide.getAttribute('aria-hidden') === 'true';
    });
  };

  const enhanceInertOnly = (slider) => {
    if (!(slider instanceof HTMLElement)) return;
    if (slider.dataset.tlA11yEnhanced === 'true' || slider.dataset.tlInertSync === 'true') return;
    slider.dataset.tlInertSync = 'true';
    syncInert(slider);
    const observer = new MutationObserver(() => syncInert(slider));
    observer.observe(slider, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true
    });
  };

  const enhanceAll = (root = document) => {
    root.querySelectorAll?.(sliderSelector).forEach(enhanceSlider);
    root.querySelectorAll?.('embla-slider').forEach((slider) => {
      if (slider.matches(sliderSelector)) return; // already fully enhanced above
      enhanceInertOnly(slider);
    });
  };

  // late-injected sliders
  let injectScanQueued = false;
  const scanForInjectedSliders = () => {
    injectScanQueued = false;
    enhanceAll(document); // idempotent (dataset-guarded)
  };
  const startInjectionObserver = () => {
    if (!document.body) return;
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (
            node instanceof HTMLElement &&
            (node.matches?.('embla-slider') || node.querySelector?.('embla-slider'))
          ) {
            if (!injectScanQueued) {
              injectScanQueued = true;
              requestAnimationFrame(scanForInjectedSliders);
            }
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    enhanceAll();
    startInjectionObserver();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    enhanceAll(event.target instanceof HTMLElement ? event.target : document);
  });
})();
